import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import FormModal from "@/components/FormModal/FormModal";
import formRenderer from "@/lib/formRenderer";
import type { FormConfig } from "@/lib/formRenderer";
import PlusIcon from "@/components/SVGComponents/PlusIcon";
import CheckIcon from "@/components/SVGComponents/CheckIcon";
import type { SelectOption } from "@/pages/CreateGathering/lib/cityOptions";
import { combineClasses, requiredMessage } from "@/lib/utils";
import toast from "@/lib/toast";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { getChapters } from "@/store/slices/chapterSlice";
import venueService from "@/services/api/venues";
import type { Venue } from "@/services/api/venues";
import type { EventType } from "@/services/api/events";
import styles from "./AddVenueModal.module.scss";

// Same three gathering types confirmed in services/api/events.ts
// (`EventType`) — real `POST /admin/venues` `suitableFor` values, not the
// display-only strings this chip list used before the endpoint was real.
const SUITABLE_FOR_OPTIONS: { label: string; value: EventType }[] = [
  { label: "Marquee", value: "MARQUEE" },
  { label: "Micro-meetup", value: "MICRO_MEETUPS" },
  { label: "Masterclass", value: "MASTERCLASS" },
];
const DEFAULT_SUITABLE_FOR: EventType[] = ["MARQUEE", "MASTERCLASS"];

// Real GET /admin/chapters fetch params — same as CitiesChapters.tsx's
// FETCH_PARAMS, given directly by the user for the "City" select here.
const CHAPTERS_FETCH_PARAMS = { pageNo: 1, perPage: 100 };

interface ChapterSelectOption extends SelectOption {
  id: number;
}


interface AddVenueFormValues {
  name: string;
  chapter: ChapterSelectOption | null;
  // address: string;
}

export interface AddVenueModalProps {
  isOpen: boolean;
  onClose: () => void;
  venueToEdit?: Venue | null;
  onSaved: () => void;
}

const AddVenueModal = ({ isOpen, onClose, venueToEdit = null, onSaved }: AddVenueModalProps) => {
  const dispatch = useAppDispatch();
  const isEditing = venueToEdit !== null;
  const chapters = useAppSelector((state) => state.chapter.chapters);
  const chapterOptions: ChapterSelectOption[] = useMemo(
    () => chapters.map((chapter) => ({ label: chapter.name, value: String(chapter.id), id: chapter.id })),
    [chapters],
  );

  useEffect(() => {
    if (isOpen) dispatch(getChapters(CHAPTERS_FETCH_PARAMS));
  }, [isOpen, dispatch]);

  const { register, handleSubmit, control, reset, formState } = useForm<AddVenueFormValues>({
    defaultValues: {
      name: "",
      chapter: null,
      // address: "",
    },
  });

  useEffect(() => {
    if (!isOpen) return;

    if (!venueToEdit) {
      reset({ name: "", chapter: null });
      setSuitableFor(DEFAULT_SUITABLE_FOR);
      return;
    }

    reset({
      name: venueToEdit.name,
      chapter: chapterOptions.find((option) => option.id === venueToEdit.chapterId) ?? null,
    });
    setSuitableFor(venueToEdit.suitableFor?.length ? venueToEdit.suitableFor : DEFAULT_SUITABLE_FOR);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, venueToEdit, chapterOptions]);

  const formConfig: FormConfig = useMemo(
    () => ({
      name: {
        controlType: "input",
        label: "Venue name",
        labelVariant: "caption",
        isRequired: true,
        placeholder: "e.g. Royal Bombay Yacht Club",
        validations: {
          required: requiredMessage("Venue name"),
          maxLength: { value: 120, message: "Venue name must be under 120 characters" },
        },
        props: { className: combineClasses(styles.fieldInput) },
      },
      chapter: {
        controlType: "select",
        label: "City",
        isRequired: true,
        labelVariant: "caption",
        isControlled: true,
        validations: { required: requiredMessage("City") },
        props: {
          options: chapterOptions,
          isSearchable: false,
          placeholder: "Select city",
          className: combineClasses(styles.fieldSelect),
        },
      },
      // address: {
      //   controlType: "input",
      //   label: "Address",
      //   isRequired: true,
      //   labelVariant: "caption",
      //   placeholder: "Street, neighbourhood",
      //   validations: {
      //     required: requiredMessage("Address"),
      //     maxLength: { value: 200, message: "Address must be under 200 characters" },
      //   },
      //   props: { className: combineClasses(styles.fieldInput) },
      // },
    }),
    [chapterOptions],
  );

  const [suitableFor, setSuitableFor] = useState<EventType[]>(DEFAULT_SUITABLE_FOR);

  const toggleSuitableFor = (option: EventType) => {
    setSuitableFor((prev) => (prev.includes(option) ? prev.filter((item) => item !== option) : [...prev, option]));
  };

  const handleClose = () => {
    reset();
    setSuitableFor(DEFAULT_SUITABLE_FOR);
    onClose();
  };

  const onSubmit = handleSubmit(async (values) => {
    if (suitableFor.length === 0) {
      toast.error("Select at least one gathering type this venue suits.");
      return;
    }

    try {
      const payload = {
        name: values.name,
        chapterId: values.chapter ? values.chapter.id : 0,
        // address: values.address,
        suitableFor,
      };

      if (isEditing && venueToEdit) {
        await venueService.updateVenue({ venueId: venueToEdit.id, ...payload });
        toast.success("Venue updated.");
      } else {
        await venueService.createVenue(payload);
        toast.success("Venue added.");
      }
      onSaved();
      handleClose();
    } catch {
      // privateRequest's response interceptor already shows an error toast
      // (see services/axios.ts) — nothing more to do here.
    }
  });

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditing ? "Edit venue" : "Add a venue"}
      subtitle={isEditing ? "Update this venue's details." : "Venues become selectable when creating a gathering."}
      onSubmit={onSubmit}
      submitLabel={isEditing ? "Save changes" : "Save venue"}
      submitIcon={<PlusIcon width={14} height={14} />}
      submitDisabled={formState.isSubmitting}
      className={combineClasses(styles.addVenueModal)}
    >
      <div className={styles.formContainer}>
        {formRenderer<AddVenueFormValues>(formConfig, formState, register, control)}

        <div className={styles.suitableForSection}>
          <div className={styles.sectionHeader}>
            <label className={styles.suitableForLabel}>
              Suitable for <span className={styles.requiredAsterisk}>*</span>
            </label>
            <span className={styles.sectionHelper}>
              Select the gathering formats this venue can host
            </span>
          </div>

          <div className={styles.chipList} role="group" aria-label="Suitable gathering types">
            {SUITABLE_FOR_OPTIONS.map((option) => {
              const checked = suitableFor.includes(option.value);
              return (
                <label
                  key={option.value}
                  className={combineClasses(styles.chip, checked && styles.chipChecked)}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleSuitableFor(option.value)}
                    disabled={formState.isSubmitting}
                    className={styles.chipInput}
                  />
                  <span className={styles.chipCheckWrapper}>
                    {checked ? (
                      <CheckIcon className={styles.chipCheckIcon} />
                    ) : (
                      <span className={styles.chipUncheckedDot} />
                    )}
                  </span>
                  <span className={styles.chipLabel}>{option.label}</span>
                </label>
              );
            })}
          </div>
        </div>
      </div>
    </FormModal>
  );
};

export default AddVenueModal;
